import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './Profile.module.scss';
import { useAuth } from '../../services/AuthContext';

// Ícones do Material UI
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import SettingsIcon from '@mui/icons-material/Settings';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import StarIcon from '@mui/icons-material/Star';
import FavoriteIcon from '@mui/icons-material/Favorite';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import LockIcon from '@mui/icons-material/Lock';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';

const Profile: React.FC = () => {
  const navigate = useNavigate();
  const { usuario } = useAuth();

  // Gera o username a partir do nome: minúsculas e espaços → underscores
  const generateUsername = (nome: string) =>
    nome.trim().toLowerCase().replace(/\s+/g, '_');

  const loggedName = usuario?.nome ?? 'Usuário';
  const loggedUsername = generateUsername(loggedName);
  const avatarInitial = loggedName.charAt(0).toUpperCase();

  const [user, setUser] = useState({
    name: loggedName,
    username: loggedUsername,
    location: "Aracaju, Sergipe",
    bio: "",
    memberSince: "—",
    totalVisits: 0,
    reviewsCount: 0,
    favorites: [
      { id: 1, name: "Mercado Central", category: "ARTESANATO", location: "Centro, Aracaju", rating: 5, image: "https://placehold.co/400x200/e2e8f0/64748b?text=Mercado" },
      { id: 2, name: "Mangue e Piche", category: "GASTRONOMIA", location: "Mosqueiro, Aracaju", rating: 5, image: "https://placehold.co/400x200/e2e8f0/64748b?text=Restaurante" }
    ],
    history: [
      { id: 1, name: "Canyon de Xingó", location: "Canindé de São Francisco, SE", date: "12 JAN 2024", image: "https://placehold.co/100x100/e2e8f0/64748b?text=Xingo" },
      { id: 2, name: "Praça São Francisco", location: "São Cristóvão, SE", date: "05 DEZ 2023", image: "https://placehold.co/100x100/e2e8f0/64748b?text=Praca" },
      { id: 3, name: "Praia do Saco", location: "Estância, SE", date: "15 OUT 2023", image: "https://placehold.co/100x100/e2e8f0/64748b?text=Praia" }
    ]
  });

  // Sincroniza o nome/username caso o contexto de auth carregue após o render inicial
  useEffect(() => {
    if (usuario?.nome) {
      setUser(prev => ({
        ...prev,
        name: usuario.nome,
        username: generateUsername(usuario.nome),
      }));
    }
  }, [usuario]);

  const [editingPhotoType, setEditingPhotoType] = useState<'avatar' | 'cover' | null>(null);
  const [currentAvatar, setCurrentAvatar] = useState<string | null>(null);
  const [currentCover, setCurrentCover] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [editName, setEditName] = useState("");
  const [editUsername, setEditUsername] = useState("");
  const [editLocation, setEditLocation] = useState("");
  const [editBio, setEditBio] = useState("");
  const [isPrivacyMenuOpen, setIsPrivacyMenuOpen] = useState(false);

  // Estado para controlar a abertura do modal de Badges
  const [isBadgesModalOpen, setIsBadgesModalOpen] = useState(false);

  // Efeito para travar o scroll da página quando qualquer um dos modais estiver ativo
  useEffect(() => {
    if (isSettingsModalOpen || editingPhotoType !== null || isBadgesModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => { document.body.style.overflow = 'auto'; };
  }, [isSettingsModalOpen, editingPhotoType, isBadgesModalOpen]);

  const handleOpenPhotoModal = (type: 'avatar' | 'cover') => { 
    setEditingPhotoType(type); 
    setSelectedImage(null); 
    setPreviewUrl(type === 'avatar' ? currentAvatar : currentCover); 
  };
  const handleClosePhotoModal = () => { 
    setEditingPhotoType(null); 
    setSelectedImage(null); 
    setPreviewUrl(null); 
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => setPreviewUrl(reader.result as string);
      reader.readAsDataURL(file);
    }
  };
  const handleSavePhoto = () => { 
    if (selectedImage) {
      if (editingPhotoType === 'avatar') setCurrentAvatar(previewUrl);
      else setCurrentCover(previewUrl);
    }
    handleClosePhotoModal(); 
  };

  const handleOpenSettingsModal = () => {
    setEditName(user.name); setEditUsername(user.username); setEditLocation(user.location); setEditBio(user.bio);   
    setIsSettingsModalOpen(true); setIsPrivacyMenuOpen(false);
  };
  const handleCloseSettingsModal = () => setIsSettingsModalOpen(false);
  const handleSaveSettings = () => {
    setUser(prev => ({ ...prev, name: editName, username: editUsername, location: editLocation, bio: editBio }));
    handleCloseSettingsModal();
  };

  return (
    <div className={styles.pageContainer}>
      
      {/* SEÇÃO: FOTO DE CAPA */}
      <div className={styles.coverPhotoContainer}>
        <div className={styles.coverPhoto} style={currentCover ? { backgroundImage: `url(${currentCover})` } : {}}>
          <button className={styles.editCoverBtn} onClick={() => handleOpenPhotoModal('cover')} title="Alterar foto de capa">
            <EditIcon fontSize="small" />
          </button>
        </div>
      </div>

      <div className={styles.mainLayout}>
        
        {/* COLUNA ESQUERDA (Sidebar) */}
        <aside className={styles.sidebar}>
          
          {/* PERFIL (Avatar e Informações Básicas) */}
          <div className={styles.profileHeader}>
            <div className={styles.avatarWrapper}>
              <img 
                src={currentAvatar || `https://placehold.co/150x150/1e293b/ffffff?text=${avatarInitial}`} 
                alt="Avatar" 
                className={styles.avatarImage} 
              />
              <button className={styles.editPhotoBtn} onClick={() => handleOpenPhotoModal('avatar')} title="Alterar foto de perfil">
                <EditIcon fontSize="small" />
              </button>
            </div>
            
            <div className={styles.nameAndSettings}>
              <div className={styles.nameTitles}>
                <h1 className={styles.userName}>{user.name}</h1>
                <p className={styles.userUsername}>@{user.username}</p>
              </div>
              <button className={styles.settingsBtn} onClick={handleOpenSettingsModal} title="Editar Perfil">
                <SettingsIcon fontSize="small" />
              </button>
            </div>
            <p className={styles.userLocation}><LocationOnIcon fontSize="small" /> {user.location}</p>
          </div>

          {/* CARD: SOBRE MIM */}
          <div className={styles.infoCard}>
            <div className={styles.cardHeader}>
              <AssignmentIndIcon className={styles.headerIcon} />
              <h3>Sobre mim</h3>
            </div>
            <p className={styles.bioText}>{user.bio}</p>

            <hr className={styles.divider}/>
            
            <div className={styles.statsList}>
              <div className={styles.statRow}>
                <span>Membro desde</span>
                <strong>{user.memberSince}</strong>
              </div>
              <div className={styles.statRow}>
                <span>Total Visitas</span>
                <strong>{user.totalVisits} locais</strong>
              </div>
              <div className={styles.statRow}>
                <span>Reviews</span>
                <strong>{user.reviewsCount} enviadas</strong>
              </div>
            </div>
          </div>

          {/* CARD: BADGES CONQUISTADAS */}
          <div className={styles.infoCard}>
            <div className={styles.cardHeaderFlex}>
              <div className={styles.cardHeaderTitle}>
                <StarIcon className={styles.headerIcon} style={{color: '#ff6b00'}} />
                <h3>Badges Conquistadas</h3>
              </div>
              {/* Evento alterado para acionar o Modal local */}
              <button className={styles.seeAllBtn} onClick={() => setIsBadgesModalOpen(true)}>Detalhes</button>
            </div>
            <div className={styles.badgesContainer}>
              <div className={styles.badgeItem} style={{backgroundColor: '#e0edff', color: '#3b82f6'}} title="Explorador de Praias: Visitou 10 praias pelo estado.">
                <BeachAccessIcon />
              </div>
              <div className={styles.badgeItem} style={{backgroundColor: '#ffedd5', color: '#f97316'}} title="Avaliador Gastronômico: Fez 5 reviews de restaurantes.">
                <RestaurantIcon />
              </div>
              <div className={styles.badgeItem} style={{backgroundColor: '#dcfce7', color: '#22c55e'}} title="Historiador: Visitou 3 museus ou praças históricas.">
                <AccountBalanceIcon />
              </div>
            </div>
          </div>

        </aside>

        {/* COLUNA DIREITA (Conteúdo Principal) */}
        <main className={styles.contentArea}>
          
          {/* SEÇÃO: MEUS FAVORITOS */}
          <section className={styles.sectionBlock}>
            <div className={styles.sectionHeader}>
              <div>
                <h2>Meus Favoritos</h2>
                <p>Locais que guardo no coração</p>
              </div>
              <button className={styles.seeAllBtn} onClick={() => navigate('/favorites')}>VER TODOS &rarr;</button>
            </div>

            <div className={styles.favoritesGrid}>
              {user.favorites.map(fav => (
                <div key={fav.id} className={styles.favoriteCard}>
                  <div className={styles.favImageWrapper}>
                    <img src={fav.image} alt={fav.name} />
                    <span className={styles.favCategory}>{fav.category}</span>
                  </div>
                  <div className={styles.favContent}>
                    <h3>{fav.name}</h3>
                    <p><LocationOnIcon fontSize="small" /> {fav.location}</p>
                    <div className={styles.favFooter}>
                      <div className={styles.stars}>
                        {[...Array(fav.rating)].map((_, i) => <StarIcon key={i} fontSize="small" />)}
                      </div>
                      <FavoriteIcon className={styles.heartIcon} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* SEÇÃO: HISTÓRICO DE VISITAS */}
          <section className={styles.sectionBlock}>
            <div className={styles.sectionHeader}>
              <div>
                <h2>Histórico de Visitas</h2>
                <p>Minha jornada pelo território sergipano</p>
              </div>
            </div>

            <div className={styles.historyList}>
              {user.history.map(item => (
                <div key={item.id} className={styles.historyCard}>
                  <img src={item.image} alt={item.name} className={styles.historyImage} />
                  <div className={styles.historyDetails}>
                    <h4>{item.name}</h4>
                    <p>{item.location}</p>
                  </div>
                  <div className={styles.historyDate}>
                    {item.date}
                  </div>
                </div>
              ))}
            </div>
          </section>

        </main>
      </div>

      {/* MODAL FOTO (CAPA OU AVATAR) */}
      {editingPhotoType !== null && (
        <div className={styles.modalOverlay} onClick={handleClosePhotoModal}>
          <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Editar Foto de {editingPhotoType === 'avatar' ? 'Perfil' : 'Capa'}</h3>
              <button className={styles.closeModalBtn} onClick={handleClosePhotoModal}><CloseIcon /></button>
            </div>
            <div className={styles.scrollableBody}>
              <div className={editingPhotoType === 'avatar' ? styles.previewAvatarWrapper : styles.previewCoverWrapper}>
                {previewUrl ? <img src={previewUrl} alt="Preview" className={editingPhotoType === 'avatar' ? styles.previewAvatarImage : styles.previewCoverImage} /> : <div className={styles.placeholderAvatar}>{avatarInitial}</div>}
              </div>
              <input type="file" accept="image/*" ref={fileInputRef} style={{display: 'none'}} onChange={handleFileChange} />
              <button className={styles.chooseImageBtn} onClick={() => fileInputRef.current?.click()}>Escolher Imagem</button>
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.cancelBtn} onClick={handleClosePhotoModal}>Cancelar</button>
              <button className={styles.saveBtn} onClick={handleSavePhoto} disabled={!selectedImage}>Salvar</button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL CONFIGURAÇÕES */}
      {isSettingsModalOpen && (
        <div className={styles.modalOverlay} onClick={handleCloseSettingsModal}>
          <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Editar Perfil</h3>
              <button className={styles.closeModalBtn} onClick={handleCloseSettingsModal}><CloseIcon /></button>
            </div>
            <div className={styles.scrollableBody}>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Nome de Exibição</label>
                  <input type="text" className={styles.inputField} value={editName} onChange={e => setEditName(e.target.value)} />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Nome de Usuário (@)</label>
                  <input type="text" className={styles.inputField} value={editUsername} onChange={e => setEditUsername(e.target.value)} />
                </div>
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Localização</label>
                <input type="text" className={styles.inputField} value={editLocation} onChange={e => setEditLocation(e.target.value)} />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Biografia</label>
                <textarea className={styles.textareaField} rows={3} value={editBio} onChange={e => setEditBio(e.target.value)} />
              </div>

              <div className={styles.privacySection}>
                <div className={styles.privacyHeader} onClick={() => setIsPrivacyMenuOpen(!isPrivacyMenuOpen)}>
                  <div className={styles.privacyHeaderLeft}><LockIcon className={styles.privacyIcon} /><span>Conta e Privacidade</span></div>
                  {isPrivacyMenuOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                </div>
                {isPrivacyMenuOpen && (
                  <div className={styles.privacyOptions}>
                    <button className={styles.privacyOptionBtn}><VpnKeyIcon fontSize="small" /> Alterar Senha</button>
                    <button className={styles.privacyOptionBtn}><VisibilityIcon fontSize="small" /> Visibilidade</button>
                    <button className={`${styles.privacyOptionBtn} ${styles.dangerBtn}`}><DeleteForeverIcon fontSize="small" /> Excluir Conta</button>
                  </div>
                )}
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.cancelBtn} onClick={handleCloseSettingsModal}>Cancelar</button>
              <button className={styles.saveBtn} onClick={handleSaveSettings}>Salvar</button>
            </div>
          </div>
        </div>
      )}

      {/* NOVO MODAL: DETALHES DAS CONQUISTAS (BADGES) */}
      {isBadgesModalOpen && (
        <div className={styles.modalOverlay} onClick={() => setIsBadgesModalOpen(false)}>
          <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Badges Conquistadas</h3>
              <button className={styles.closeModalBtn} onClick={() => setIsBadgesModalOpen(false)}>
                <CloseIcon />
              </button>
            </div>
            
            <div className={styles.scrollableBody}>
              <div className={styles.modalBadgeList}>
                
                <div className={styles.modalBadgeRow}>
                  <div className={styles.badgeItem} style={{backgroundColor: '#e0edff', color: '#3b82f6'}}>
                    <BeachAccessIcon />
                  </div>
                  <div className={styles.modalBadgeInfo}>
                    <h4>Explorador de Praias</h4>
                    <p>Visitou 10 praias pelo estado de Sergipe.</p>
                  </div>
                </div>

                <div className={styles.modalBadgeRow}>
                  <div className={styles.badgeItem} style={{backgroundColor: '#ffedd5', color: '#f97316'}}>
                    <RestaurantIcon />
                  </div>
                  <div className={styles.modalBadgeInfo}>
                    <h4>Avaliador Gastronômico</h4>
                    <p>Fez 5 reviews detalhadas de restaurantes locais.</p>
                  </div>
                </div>

                <div className={styles.modalBadgeRow}>
                  <div className={styles.badgeItem} style={{backgroundColor: '#dcfce7', color: '#22c55e'}}>
                    <AccountBalanceIcon />
                  </div>
                  <div className={styles.modalBadgeInfo}>
                    <h4>Historiador</h4>
                    <p>Visitou 3 museus ou praças com patrimônio histórico.</p>
                  </div>
                </div>

              </div>
            </div>

            <div className={styles.modalFooter}>
              <button className={styles.saveBtn} onClick={() => setIsBadgesModalOpen(false)}>Fechar</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Profile;